import React, { useState } from "react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Phone, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * "Request a Callback" lead-capture widget — Max Healthcare-style.
 * Public endpoint POST /api/leads/callback (no auth required).
 * Persisted to db.callback_leads; admin sees them in /api/admin/leads.
 */
export default function CallbackForm({ compact = false, source = "landing" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", whatsapp: "", query: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Please enter your name"); return;
    }
    if (!/^\+?\d[\d\s\-()]{9,14}$/.test(form.phone.trim())) {
      toast.error("Enter a valid mobile number (10-15 digits)"); return;
    }
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("Enter a valid email address"); return;
    }
    if (form.whatsapp.trim() && !/^\+?\d[\d\s\-()]{9,14}$/.test(form.whatsapp.trim())) {
      toast.error("Enter a valid WhatsApp number"); return;
    }
    setBusy(true);
    try {
      await api.post("/leads/callback", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        query: form.query.trim() || null,
        source,
      });
      setDone(true);
      toast.success("Got it! Our health advisor will call you shortly.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Card className={`border-teal-200 bg-gradient-to-br from-white to-teal-50/40 ${compact ? "p-5" : "p-6"}`} data-testid="callback-form-done">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700 mb-3">
          <CheckCircle2 className="w-6 h-6"/>
        </div>
        <h3 className="font-heading text-lg font-semibold text-slate-900">You&apos;re on the list</h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          Our health advisor will ring <b className="text-slate-900">{form.phone}</b> within
          the next business hour. Keep your MRI/CT scans handy if you have any.
        </p>
        <a
          href="https://wa.me/918317674160"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-teal-700 text-sm font-semibold hover:underline"
        >
          Or chat now on WhatsApp →
        </a>
      </Card>
    );
  }

  return (
    <Card
      className={`border-teal-200 bg-gradient-to-br from-white to-teal-50/40 ${compact ? "p-5" : "p-6"}`}
      data-testid="callback-form"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-600 to-teal-500 grid place-items-center text-white shadow-sm">
          <Phone className="w-5 h-5"/>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-teal-700">Free · no obligation</div>
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 leading-tight">
            Get a call back from a Health Advisor
          </h3>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input
          required placeholder="Full name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="callback-name-input"
          className="h-11"
        />
        <Input
          required placeholder="+91 Mobile number"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="callback-phone-input"
          inputMode="tel"
          className="h-11"
        />
        <Input
          type="email"
          placeholder="Email (optional — for receipt + report copy)"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="callback-email-input"
          inputMode="email"
          className="h-11"
        />
        <Input
          placeholder="WhatsApp number (optional, if different)"
          value={form.whatsapp}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
          data-testid="callback-whatsapp-input"
          inputMode="tel"
          className="h-11"
        />
        {!compact && (
          <Textarea
            placeholder="Your condition / question (optional)"
            value={form.query}
            onChange={(e) => setForm({ ...form, query: e.target.value })}
            data-testid="callback-query-input"
            className="min-h-[72px] resize-none"
            maxLength={500}
          />
        )}
        <Button
          type="submit"
          disabled={busy}
          className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white font-semibold"
          data-testid="callback-submit-btn"
        >
          {busy ? "Submitting…" : "Request call back"}
        </Button>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 pt-1">
          <ShieldCheck className="w-3.5 h-3.5 text-teal-600"/>
          Your data stays private · No spam, ever
        </div>
      </form>
    </Card>
  );
}
